import { useMemo } from 'react';

import { useGetApiV1Person } from '@/shared/api/generated';

import type { TutorFilterOption } from '../types/tutorAi';
import { useLookupSearchFilter } from './useLookupSearchFilter';

/** Quantidade de sugestões pedidas por busca — é um autocomplete, não uma listagem. */
const STUDENT_SEARCH_PAGE_SIZE = 10;

/**
 * Busca de aluno por nome, para a entrada da tela de uso e limites.
 *
 * Consome `GET /api/v1/person`, que pertence ao controller **`person`** — é a única
 * dependência de permissão externa do módulo, porque não existe lookup de pessoa fora
 * dele. Um operador sem acesso a Pessoas não conseguirá buscar o aluno.
 *
 * `PersonGridRepositoryResponse` não tem campo `name`: o rótulo é montado juntando
 * `firstName` e `lastName`, ambos opcionais e nullable, com o e-mail como reserva.
 *
 * @param filter - Texto digitado no campo de busca
 * @returns Opções encontradas e se a última query concluiu com sucesso
 *
 * @example
 * ```tsx
 * const { items } = useTutorStudentSearch(search);
 * ```
 */
export function useTutorStudentSearch(filter: string) {
  const { debouncedFilter, isReady, isPending } = useLookupSearchFilter(filter);

  const { data, isSuccess } = useGetApiV1Person(
    isReady
      ? { people: debouncedFilter, page: 1, pageSize: STUDENT_SEARCH_PAGE_SIZE, active: true }
      : undefined,
    { query: { enabled: isReady } },
  );

  const items: readonly TutorFilterOption[] = useMemo(() => {
    if (isPending) return [];

    return (data?.data ?? []).reduce<TutorFilterOption[]>((options, person) => {
      // Pessoa sem id não abre relatório — deixá-la na lista daria uma opção que não faz nada.
      if (person.id === undefined || person.id === null) return options;

      // O e-mail é a reserva do rótulo: sem nome, a opção viria em branco e o operador veria
      // uma linha que não consegue identificar. Sem nada que identifique, ela sai da lista.
      const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
      const label = name || (person.email ?? '').trim();
      if (label === '') return options;

      options.push({ value: String(person.id), label });
      return options;
    }, []);
  }, [isPending, data]);

  return { items, isQuerySuccess: !isPending && isSuccess } as const;
}
